import React, { useMemo } from 'react';
import { Droplet, RotateCcw, Wrench } from 'lucide-react';
import { BaseType } from '../../../../../types/Enums';
import { getChartColor } from '../../../../../Helpers/chartColors';
import { BASE_TYPE_GROUPS, presentBaseTypeGroupLabel } from '../../../../../Helpers/baseTypeUtils';
import { ActivityType } from '../../../../../Helpers/maintenanceCostUtils';

// Timeline configuration constants
const YEAR_LABEL_THRESHOLDS = [
    { maxYears: 10, interval: 1 },
    { maxYears: 20, interval: 2 },
    { maxYears: 40, interval: 5 }
] as const;
const DEFAULT_LABEL_INTERVAL = 10;

/**
 * Calculate year label interval based on timeline length
 */
const getYearLabelInterval = (totalYears: number): number => {
    for (const { maxYears, interval } of YEAR_LABEL_THRESHOLDS) {
        if (totalYears <= maxYears) return interval;
    }
    return DEFAULT_LABEL_INTERVAL;
};

/** One timeline dot: a year's activities within one base-type group, as one chart bar segment */
interface GroupActivity {
    key: string;
    /** The group's base types that have activities this year */
    baseTypes: string[];
    label: string;
    replacementCount: number;
    maintenanceCount: number;
    color: string;
}

interface YearActivities {
    year: number;
    groups: GroupActivity[];
}

interface TooltipState {
    content: React.ReactNode;
    x: number;
    y: number;
}

interface CombinedTimelineProps {
    /** Pre-computed year data with baseType groups from DraftPreviewContent */
    yearsData: Array<{
        year: number;
        totalCost: number;
        hasActivities: boolean;
        baseTypeGroups: Array<{
            baseType: string;
            label: string;
            totalCost: number;
            activities: Array<{
                activityType: ActivityType;
            }>;
        }>;
    }>;
    /** Optional tooltip setter for styled tooltips */
    setTooltip?: (tooltip: TooltipState | null) => void;
    /** Makes the activity dots clickable; called with the dot's year and its group's base types */
    onActivityClick?: (year: number, baseTypes: readonly string[]) => void;
    /** Hide the legend (when using a shared legend elsewhere) */
    hideLegend?: boolean;
    /** Hide the header/title */
    hideHeader?: boolean;
}

/**
 * Shared legend component for chart and timeline
 */
interface ChartTimelineLegendProps {
    yearsData: CombinedTimelineProps['yearsData'];
}

export const ChartTimelineLegend: React.FC<ChartTimelineLegendProps> = ({ yearsData }) => {
    // Collect all active baseTypes
    const activeBaseTypes = new Set<string>();
    yearsData.forEach(yearData => {
        yearData.baseTypeGroups.forEach(group => {
            if (group.totalCost > 0 || group.activities.length > 0) {
                activeBaseTypes.add(group.baseType);
            }
        });
    });

    return (
        <div className="combined-timeline-legend">
            {BASE_TYPE_GROUPS.map((group, index) => {
                // Check if this group has any activities
                const hasActivities = group.baseTypes.some(bt => activeBaseTypes.has(bt));
                if (!hasActivities) return null;

                return (
                    <div key={group.key} className="legend-item">
                        <span
                            className="legend-color"
                            style={{ backgroundColor: getChartColor(index) }}
                        />
                        <span className="legend-label">{presentBaseTypeGroupLabel(group, bt => activeBaseTypes.has(bt))}</span>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Combined timeline visualization for all building components.
 * Aggregates activities by BaseType and shows them on a single timeline.
 */
export const CombinedTimeline: React.FC<CombinedTimelineProps> = ({ yearsData, setTooltip, onActivityClick, hideLegend = false, hideHeader = false }) => {
    const currentYear = new Date().getFullYear();

    // Transform yearsData into timeline-friendly format
    const { timelineData, startYear, endYear } = useMemo(() => {
        if (yearsData.length === 0) {
            return { timelineData: [], startYear: currentYear, endYear: currentYear + 10 };
        }

        // Get years range
        const years = yearsData.map(y => y.year);
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);

        // One dot per BASE_TYPE_GROUPS entry, coloured like its chart segment and legend entry.
        // A base type outside every group gets a grey dot of its own.
        const data: YearActivities[] = yearsData
            .filter(y => y.hasActivities)
            .map(yearData => {
                const groups = new Map<string, GroupActivity & { index: number }>();
                yearData.baseTypeGroups.forEach(btGroup => {
                    if (btGroup.activities.length === 0) return;
                    const index = BASE_TYPE_GROUPS.findIndex(g => g.baseTypes.includes(btGroup.baseType as BaseType));
                    const key = index === -1 ? btGroup.baseType : BASE_TYPE_GROUPS[index].key;
                    let group = groups.get(key);
                    if (!group) {
                        group = {
                            key,
                            index: index === -1 ? 999 : index,
                            baseTypes: [],
                            label: index === -1 ? btGroup.label : '',
                            replacementCount: 0,
                            maintenanceCount: 0,
                            color: index === -1 ? '#6c757d' : getChartColor(index)
                        };
                        groups.set(key, group);
                    }
                    group.baseTypes.push(btGroup.baseType);
                    group.replacementCount += btGroup.activities.filter(a => a.activityType === 'replacement').length;
                    group.maintenanceCount += btGroup.activities.filter(a => a.activityType === 'maintenance').length;
                });

                // Last group nearest the line, so the first group sits at the bottom of the stack
                const sorted = [...groups.values()]
                    .map(group => group.index === 999 ? group : {
                        ...group,
                        label: presentBaseTypeGroupLabel(BASE_TYPE_GROUPS[group.index], bt => group.baseTypes.includes(bt))
                    })
                    .sort((a, b) => b.index - a.index);

                return {
                    year: yearData.year,
                    groups: sorted
                };
            });

        return {
            timelineData: data,
            startYear: minYear,
            endYear: maxYear,
        };
    }, [yearsData, currentYear]);

    // Don't render if no data
    if (timelineData.length === 0) {
        return null;
    }

    const totalYears = endYear - startYear;
    const labelInterval = getYearLabelInterval(totalYears);

    // Calculate max stacked markers to determine needed height
    const maxStackedMarkers = Math.max(1, ...timelineData.map(y => y.groups.length));
    // The line sits near the bottom with the year labels below it and the stacks grow up from it,
    // so the height must hold the tallest stack or the dots run into whatever sits above.
    const markerHeight = 34; // dot + gap
    const baseHeight = 40; // year labels below the line and the gap between line and stack
    const timelineHeight = baseHeight + (maxStackedMarkers * markerHeight);

    return (
        <div className={`combined-timeline ${hideHeader ? 'no-header' : ''}`}>
            {!hideHeader && (
                <div className="combined-timeline-header">
                    <h6 className="combined-timeline-title">Aktiviteter per komponenttyp</h6>
                </div>
            )}

            <div className="combined-timeline-track">
                <div className="timeline-inner" style={{ height: timelineHeight }}>
                    <div className="timeline-line"></div>

                    {/* Year markers */}
                    {Array.from({ length: totalYears + 1 }, (_, i) => {
                        const year = startYear + i;
                        const isCurrentYear = year === currentYear;
                        const position = totalYears > 0 ? (i / totalYears) * 100 : 0;
                        const isLastYear = i === totalYears;
                        const shouldShowLabel = i % labelInterval === 0 || isCurrentYear || isLastYear;

                        return (
                            <div
                                key={`year-${year}`}
                                className={`timeline-year-marker ${isCurrentYear ? 'current-year' : ''}`}
                                style={{ left: `${position}%` }}
                            >
                                <div
                                    className="year-dot"
                                    title={!shouldShowLabel ? year.toString() : undefined}
                                ></div>
                                {shouldShowLabel && <span className="year-label">{year}</span>}
                            </div>
                        );
                    })}

                    {/* Activity markers by year */}
                    {timelineData.map(yearData => {
                        const position = totalYears > 0 ? ((yearData.year - startYear) / totalYears) * 100 : 0;
                        const isOverdue = yearData.year < currentYear;

                        return (
                            <div
                                key={`activities-${yearData.year}`}
                                className="combined-timeline-year-activities"
                                style={{ left: `${position}%` }}
                            >
                                {yearData.groups.map(group => {
                                    const hasReplacement = group.replacementCount > 0;
                                    const hasMaintenance = group.maintenanceCount > 0;

                                    // Build tooltip parts
                                    const parts: string[] = [];
                                    if (hasMaintenance) {
                                        parts.push(`${group.maintenanceCount} underhåll`);
                                    }
                                    if (hasReplacement) {
                                        parts.push(`${group.replacementCount} ${group.replacementCount === 1 ? 'byte' : 'byten'}`);
                                    }
                                    const tooltipText = `${group.label} (${yearData.year}): ${parts.join(', ')}`;

                                    return (
                                        <div
                                            key={`${yearData.year}-${group.key}`}
                                            className={`combined-activity-marker ${isOverdue ? 'overdue' : ''} ${onActivityClick ? 'clickable' : ''}`}
                                            title={!setTooltip ? tooltipText : undefined}
                                            role={onActivityClick ? 'button' : undefined}
                                            tabIndex={onActivityClick ? 0 : undefined}
                                            aria-label={onActivityClick ? tooltipText : undefined}
                                            onClick={onActivityClick ? () => onActivityClick(yearData.year, group.baseTypes) : undefined}
                                            onKeyDown={onActivityClick ? (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onActivityClick(yearData.year, group.baseTypes);
                                                }
                                            } : undefined}
                                            onMouseEnter={setTooltip ? (e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                    content: tooltipText,
                                                    x: rect.right,
                                                    y: rect.top + rect.height / 2
                                                });
                                            } : undefined}
                                            onMouseLeave={setTooltip ? () => setTooltip(null) : undefined}
                                        >
                                            <div
                                                className="activity-dot"
                                                style={{ backgroundColor: group.color }}
                                            >
                                                {group.baseTypes.length === 1 && group.baseTypes[0] === 'roof_drainage' ? (
                                                    <Droplet size={10} />
                                                ) : hasReplacement ? (
                                                    <RotateCcw size={10} />
                                                ) : (
                                                    <Wrench size={10} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Legend */}
            {!hideLegend && (
                <div className="combined-timeline-legend">
                    {BASE_TYPE_GROUPS.map((group, index) => {
                        // Check if this group has any activities
                        const hasActivities = timelineData.some(y => y.groups.some(g => g.key === group.key));
                        if (!hasActivities) return null;

                        return (
                            <div key={group.key} className="legend-item">
                                <span
                                    className="legend-color"
                                    style={{ backgroundColor: getChartColor(index) }}
                                />
                                <span className="legend-label">
                                    {presentBaseTypeGroupLabel(group, baseType =>
                                        timelineData.some(y => y.groups.some(g => g.baseTypes.includes(baseType)))
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
